import React, { useState, useEffect, useContext } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Search, ExternalLink, ArrowLeft, Filter, Loader2, AlertCircle, Lock, Home, ChevronLeft, ChevronRight, Compass, Copy, CheckCheck, Clock, Code, Globe, MessageSquare, GitBranch } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AuthContext, API } from "@/App";
import { toast } from "sonner";
import { ReviewForm } from "@/components/Reviews";
import { MatchRating } from "@/components/MatchRating";

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchLimitReached, setSearchLimitReached] = useState(false);
  const [budget, setBudget] = useState("any");
  const [skillLevel, setSkillLevel] = useState("any");
  const [analysis, setAnalysis] = useState(null);
  const [promptTier, setPromptTier] = useState("basic");
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [decomposition, setDecomposition] = useState(null);
  const [decomposing, setDecomposing] = useState(false);
  const [reEvalIdx, setReEvalIdx] = useState(null);
  const [reEvalFeedback, setReEvalFeedback] = useState("");
  const [reEvalType, setReEvalType] = useState("not_accurate");
  const [reEvalLoading, setReEvalLoading] = useState(false);
  const [reEvalResult, setReEvalResult] = useState(null);
  const [autofillLoading, setAutofillLoading] = useState(null);
  const [autofillResult, setAutofillResult] = useState(null);

  const hasPremiumAccess = user?.is_admin || user?.is_vip || ["premium", "team"].includes(user?.subscription_tier);
  const hasProAccess = user?.is_admin || user?.is_vip || ["pro", "premium", "team"].includes(user?.subscription_tier);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q) { setQuery(q); performSearch(q); }
  }, [searchParams]);

  const performSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;
    setLoading(true); setError(null); setSearchLimitReached(false);

    try {
      const res = await fetch(`${API}/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ prompt: searchQuery, budget: budget !== "any" ? budget : null, skill_level: skillLevel !== "any" ? skillLevel : null })
      });

      if (res.status === 403) {
        const data = await res.json();
        if (data.detail?.upgrade_required) { setSearchLimitReached(true); setError(data.detail.message); }
        else { setError(data.detail?.message || data.detail || "Search limit reached"); }
        return;
      }
      if (!res.ok) throw new Error("Search failed");

      const data = await res.json();

      // Background job: poll until the 8-step engine completes (avoids gateway timeout)
      if (data.job_id) {
        const started = Date.now();
        while (Date.now() - started < 180000) {
          await new Promise(r => setTimeout(r, 2500));
          try {
            const sRes = await fetch(`${API}/search/status/${data.job_id}`, { credentials: "include" });
            if (!sRes.ok) continue;
            const job = await sRes.json();
            if (job.status === "complete" && job.result) {
              setResults(job.result.results || []);
              setAnalysis(job.result.analysis || null);
              setPromptTier(job.result.prompt_tier || "basic");
              return;
            }
            if (job.status === "failed") throw new Error(job.error || "Search failed");
          } catch (pollErr) {
            if (pollErr.message && pollErr.message !== "Failed to fetch") throw pollErr;
          }
        }
        throw new Error("Search timed out");
      }

      setResults(data.results || []);
      setAnalysis(data.analysis || null);
      setPromptTier(data.prompt_tier || "basic");
    } catch (e) {
      setError("Failed to search. Please try again.");
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  const handleCopy = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    toast.success("Prompt copied!");
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const getConfidenceStyle = (confidence) => {
    switch (confidence) {
      case "high": return { bg: "bg-emerald-500/15", text: "text-emerald-400", border: "border-emerald-500/30" };
      case "medium": return { bg: "bg-yellow-500/15", text: "text-yellow-400", border: "border-yellow-500/30" };
      case "low": return { bg: "bg-orange-500/15", text: "text-orange-400", border: "border-orange-500/30" };
      default: return { bg: "bg-red-500/15", text: "text-red-400", border: "border-red-500/30" };
    }
  };

  const getRankEmoji = (idx) => {
    return ["🥇", "🥈", "🥉", "4.", "5."][idx] || `${idx + 1}.`;
  };

  const handleDecompose = async () => {
    if (!query.trim()) return;
    setDecomposing(true);
    try {
      const res = await fetch(`${API}/decompose`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ task: query })
      });
      if (res.ok) {
        setDecomposition(await res.json());
      } else {
        const err = await res.json();
        toast.error(err.detail || "Decomposition failed");
      }
    } catch (e) { toast.error("Something went wrong"); }
    finally { setDecomposing(false); }
  };

  const handleReEvaluate = async (result, idx) => {
    if (!reEvalFeedback.trim()) { toast.error("Please describe what was missing"); return; }
    setReEvalLoading(true); setReEvalResult(null);
    try {
      const res = await fetch(`${API}/search/re-evaluate`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          original_prompt: query, optimized_prompt: result.optimized_prompt,
          tool_name: result.tool?.name || "", feedback: reEvalFeedback, feedback_type: reEvalType
        })
      });
      if (res.ok) { setReEvalResult(await res.json()); toast.success("V2 prompt generated!"); }
      else { const e = await res.json(); toast.error(e.detail || "Re-evaluation failed"); }
    } catch (e) { toast.error("Something went wrong"); }
    finally { setReEvalLoading(false); }
  };

  const handleAutofill = async (result, idx) => {
    setAutofillLoading(idx);
    try {
      const res = await fetch(`${API}/search/autofill`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ task: query, tool_name: result.tool?.name })
      });
      if (res.ok) { setAutofillResult({ idx, data: await res.json() }); toast.success("Autofill complete!"); }
      else { const e = await res.json(); toast.error(e.detail || "Autofill failed"); }
    } catch (e) { toast.error("Something went wrong"); }
    finally { setAutofillLoading(null); }
  };

  return (
    <div className="min-h-screen bg-dark">
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white/60 hover:text-white" data-testid="back-button"><ChevronLeft className="w-5 h-5" /></Button>
              <Button variant="ghost" size="icon" onClick={() => navigate(1)} className="text-white/60 hover:text-white" data-testid="forward-button"><ChevronRight className="w-5 h-5" /></Button>
            </div>
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-purple-700 flex items-center justify-center"><Compass className="w-4 h-4 text-white" /></div>
              <span className="font-inter font-bold text-lg hidden sm:inline">promptviser</span>
            </Link>
            <form onSubmit={handleSearch} className="flex-1 max-w-2xl">
              <div className="relative flex items-center bg-dark-paper border border-white/10 rounded-lg overflow-hidden">
                <Search className="w-5 h-5 text-white/30 ml-4" />
                <Input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Describe what you want to build..."
                  className="flex-1 bg-transparent border-0 py-3 px-3 placeholder:text-white/25 focus-visible:ring-0 text-sm" data-testid="search-input" />
                <Button type="submit" size="sm" className="m-1.5 bg-purple-700 text-white hover:bg-purple-600 text-xs" data-testid="search-button">Search</Button>
              </div>
            </form>
            <Link to="/"><Button variant="ghost" size="icon" className="text-white/60 hover:text-white"><Home className="w-5 h-5" /></Button></Link>
            {hasProAccess && <Link to="/chat"><Button variant="outline" size="sm" className="border-purple-500/40 text-purple-300 text-xs"><MessageSquare className="w-3.5 h-3.5 mr-1" />Chat</Button></Link>}
            {user ? (
              <Link to="/dashboard"><Button variant="outline" size="sm" className="border-white/20 text-xs">Dashboard</Button></Link>
            ) : (
              <Link to="/login"><Button size="sm" className="bg-purple-700 text-white hover:bg-purple-600 text-xs">Sign In</Button></Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <Link to="/" className="flex items-center gap-2 text-white/40 hover:text-white transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <Filter className="w-4 h-4 text-white/30" />
            <Select value={budget} onValueChange={setBudget}>
              <SelectTrigger className="w-28 bg-dark-paper border-white/10 text-xs" data-testid="budget-filter"><SelectValue placeholder="Budget" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any Budget</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="low">$0-20</SelectItem>
                <SelectItem value="medium">$20-50</SelectItem>
                <SelectItem value="high">$50+</SelectItem>
              </SelectContent>
            </Select>
            <Select value={skillLevel} onValueChange={setSkillLevel}>
              <SelectTrigger className="w-28 bg-dark-paper border-white/10 text-xs" data-testid="skill-filter"><SelectValue placeholder="Skill" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any Level</SelectItem>
                <SelectItem value="beginner">Beginner</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => performSearch(query)} className="border-white/20 text-xs" data-testid="apply-filters">Apply</Button>
          </div>
        </div>

        {query && (
          <div className="mb-6">
            <p className="text-white/40 text-xs mb-1">Showing results for:</p>
            <p className="font-mono text-sm text-purple-300 bg-dark-paper px-4 py-3 rounded-lg border border-purple-500/20">"{query}"</p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative mb-4">
              <div className="w-3 h-3 rounded-full bg-neon animate-neon-pulse" />
              <div className="absolute inset-0 w-3 h-3 rounded-full bg-neon/30 animate-ping" />
            </div>
            <Loader2 className="w-8 h-8 text-purple-400 animate-spin mb-3" />
            <p className="text-white/50 text-sm">Running 8-step reasoning engine...</p>
          </div>
        )}

        {/* Search Limit */}
        {searchLimitReached && !loading && (
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-8 text-center">
            <Lock className="w-10 h-10 text-amber-400 mx-auto mb-4" />
            <h3 className="font-inter font-semibold text-lg mb-2">Search Limit Reached</h3>
            <p className="text-white/50 mb-6 max-w-md mx-auto text-sm">{error}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/pricing"><Button className="bg-purple-700 text-white hover:bg-purple-600 font-semibold">Upgrade to Pro - $15/mo</Button></Link>
              <Link to="/pricing"><Button variant="outline" className="border-white/20">View All Plans</Button></Link>
            </div>
          </div>
        )}

        {/* Error */}
        {error && !searchLimitReached && !loading && (
          <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-6 text-center">
            <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
            <p className="text-red-400 mb-4 text-sm">{error}</p>
            <Button onClick={() => performSearch(query)} variant="outline" className="border-white/20 text-sm">Try Again</Button>
          </div>
        )}

        {/* Results - Surgical Matching Format */}
        {!loading && !error && results.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <Badge className="bg-purple-500/15 text-purple-300 border-purple-500/30 text-xs">
                {promptTier.charAt(0).toUpperCase() + promptTier.slice(1)} Prompts
              </Badge>
              {promptTier === "basic" && (
                <span className="text-xs text-white/30">
                  <Link to="/pricing" className="text-purple-400 hover:underline">Upgrade</Link> for advanced prompts
                </span>
              )}
              <span className="text-xs text-white/30 ml-auto">{results.length} matches found</span>
            </div>

            {/* Analysis */}
            {analysis && (
              <div className="bg-dark-paper border border-white/10 rounded-lg p-5 mb-4">
                <h3 className="font-semibold text-white/70 mb-3 text-sm">AI Analysis</h3>
                <div className="grid sm:grid-cols-3 gap-4 text-xs">
                  {analysis.task_type && (
                    <div><span className="text-white/30">Task Type:</span><p className="text-white/70 mt-0.5 capitalize">{analysis.task_type}</p></div>
                  )}
                  {analysis.categories && (
                    <div><span className="text-white/30">Categories:</span>
                      <div className="flex flex-wrap gap-1 mt-0.5">{analysis.categories.map((c, i) => <Badge key={i} variant="secondary" className="text-[10px]">{c}</Badge>)}</div>
                    </div>
                  )}
                  {analysis.complexity && (
                    <div><span className="text-white/30">Complexity:</span><p className="text-white/70 mt-0.5 capitalize">{analysis.complexity}</p></div>
                  )}
                </div>
              </div>
            )}

            {/* Match Cards */}
            {results.map((result, index) => {
              const cs = getConfidenceStyle(result.confidence);
              return (
                <div key={result.tool?.tool_id || index} className="bg-dark-paper border border-white/10 rounded-xl overflow-hidden card-hover" data-testid={`search-result-${index}`}>
                  <div className="p-5">
                    <div className="flex flex-col md:flex-row md:items-start gap-5">
                      {/* Match Score */}
                      <div className="flex md:flex-col items-center gap-2 md:gap-1 md:min-w-[80px]">
                        <div className="text-2xl font-bold">{getRankEmoji(index)}</div>
                        <div className="w-16 h-16 rounded-xl bg-purple-900/30 border border-purple-500/30 flex flex-col items-center justify-center">
                          <span className="font-inter font-black text-2xl text-purple-300">{result.match_percentage}</span>
                          <span className="text-[10px] text-purple-400/70">%</span>
                        </div>
                        <Badge className={`${cs.bg} ${cs.text} ${cs.border} text-[10px]`}>
                          {result.confidence_emoji} {result.confidence}
                        </Badge>
                      </div>

                      {/* Tool Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h3 className="font-inter font-semibold text-lg">{result.tool?.name}</h3>
                          <Badge variant="outline" className="border-white/20 text-white/50 text-[10px]">{result.tool?.price}</Badge>
                          {result.tool?.supports_agentic && <Badge className="bg-purple-500/15 text-purple-300 text-[10px]">Agentic</Badge>}
                          {result.tool?.supports_programmatic && <Badge className="bg-neon/10 text-neon text-[10px]">Programmatic</Badge>}
                        </div>

                        {/* Why this AI */}
                        <p className="text-white/60 text-sm mb-3">{result.why_this_ai || result.tool?.description}</p>

                        {/* Programming Languages */}
                        {result.tool?.programming_languages?.length > 0 && (
                          <div className="mb-2">
                            <span className="text-[10px] uppercase tracking-wider text-white/30 mr-2">Languages:</span>
                            <span className="text-xs text-white/50">{result.tool.programming_languages.join(", ")}</span>
                          </div>
                        )}

                        {/* Keywords/Techniques */}
                        {result.relevant_keywords_techniques?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {result.relevant_keywords_techniques.map((kw, i) => (
                              <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-purple-900/30 text-purple-300 border border-purple-500/20">{kw}</span>
                            ))}
                          </div>
                        )}

                        {/* Capabilities grid */}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-3 text-xs">
                          <div className="text-white/30">Supports agentic: <span className="text-white/60">{result.tool?.supports_agentic ? "Yes" : "No"}</span></div>
                          <div className="text-white/30">Supports programmatic: <span className="text-white/60">{result.tool?.supports_programmatic ? "Yes" : "No"}</span></div>
                          {result.tool?.context_window && <div className="text-white/30">Context window: <span className="text-white/60">{result.tool.context_window}</span></div>}
                          {result.tool?.seo_capable && <div className="text-white/30">SEO capable: <span className="text-neon">Yes</span></div>}
                        </div>

                        {/* Optimized Prompt */}
                        <div className={`bg-dark rounded-lg p-4 border border-white/5 relative ${copiedIdx === index ? "copy-flash" : ""}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <p className="text-[10px] uppercase tracking-wider text-purple-400">Optimized Prompt</p>
                            <Badge variant="outline" className="text-[10px] border-purple-500/30 text-purple-300">{promptTier}</Badge>
                            <button
                              onClick={() => handleCopy(result.optimized_prompt, index)}
                              className="ml-auto p-1.5 rounded hover:bg-white/5 transition-colors"
                              data-testid={`copy-prompt-${index}`}
                            >
                              {copiedIdx === index ? <CheckCheck className="w-3.5 h-3.5 text-neon" /> : <Copy className="w-3.5 h-3.5 text-white/30" />}
                            </button>
                          </div>
                          <p className="font-mono text-xs text-white/70 leading-relaxed">{result.optimized_prompt}</p>
                          {promptTier === "basic" && (
                            <p className="text-[10px] text-white/30 mt-2">
                              <Link to="/pricing" className="text-purple-400 hover:underline">Upgrade to Pro</Link> for advanced prompts
                            </p>
                          )}
                        </div>

                        {/* Re-evaluate & Autofill Buttons */}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {hasProAccess && (
                            <button onClick={() => setReEvalIdx(reEvalIdx === index ? null : index)} className="text-[10px] text-purple-400 hover:text-purple-300 flex items-center gap-1" data-testid={`re-evaluate-btn-${index}`}>
                              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 4v6h6M23 20v-6h-6M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15"/></svg>
                              Re-evaluate
                            </button>
                          )}
                          {hasPremiumAccess && (
                            <button onClick={() => handleAutofill(result, index)} disabled={autofillLoading === index} className="text-[10px] text-neon hover:text-neon/80 flex items-center gap-1" data-testid={`autofill-btn-${index}`}>
                              {autofillLoading === index ? <Loader2 className="w-3 h-3 animate-spin" /> : <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>}
                              Surprise Me
                            </button>
                          )}
                        </div>

                        {/* Re-evaluate Panel */}
                        {reEvalIdx === index && (
                          <div className="mt-3 bg-dark rounded-lg p-3 border border-purple-500/20">
                            <p className="text-[10px] uppercase tracking-wider text-purple-400 mb-2">What was missing?</p>
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {[{v:"not_accurate",l:"Not accurate"},{v:"wrong_format",l:"Wrong format"},{v:"missing_details",l:"Missing details"},{v:"too_vague",l:"Too vague"},{v:"other",l:"Other"}].map(opt => (
                                <button key={opt.v} onClick={() => setReEvalType(opt.v)} className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${reEvalType === opt.v ? "border-purple-500 bg-purple-500/20 text-purple-300" : "border-white/10 text-white/40 hover:border-white/20"}`}>{opt.l}</button>
                              ))}
                            </div>
                            <Input value={reEvalFeedback} onChange={e => setReEvalFeedback(e.target.value)} placeholder="Describe what needs improvement..." className="bg-dark-subtle border-white/10 text-xs mb-2" data-testid={`re-evaluate-input-${index}`} />
                            <Button onClick={() => handleReEvaluate(result, index)} disabled={reEvalLoading} size="sm" className="bg-purple-700 text-white hover:bg-purple-600 text-xs" data-testid={`re-evaluate-submit-${index}`}>
                              {reEvalLoading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Generating V2...</> : "Generate V2 Prompt"}
                            </Button>
                            {reEvalResult && (
                              <div className="mt-3 space-y-2">
                                <div className="bg-dark-subtle rounded-lg p-3 border border-white/5">
                                  <p className="text-[10px] text-white/30 mb-1">V2 Prompt (Improved)</p>
                                  <p className="font-mono text-xs text-white/70">{reEvalResult.v2_prompt}</p>
                                </div>
                                <div className="flex flex-wrap gap-1">{reEvalResult.changes_made?.map((c, i) => <Badge key={i} className="bg-neon/10 text-neon text-[10px]">{c}</Badge>)}</div>
                                <p className="text-[10px] text-white/40">{reEvalResult.explanation}</p>
                                <Button variant="outline" size="sm" onClick={() => handleCopy(reEvalResult.v2_prompt, `v2-${index}`)} className="border-white/20 text-[10px]"><Copy className="w-3 h-3 mr-1" />Copy V2</Button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Autofill Result */}
                        {autofillResult?.idx === index && (
                          <div className="mt-3 bg-dark rounded-lg p-3 border border-neon/20">
                            <p className="text-[10px] uppercase tracking-wider text-neon mb-2">Surprise Me Result</p>
                            <div className="grid grid-cols-3 gap-2 mb-2 text-xs">
                              <div><span className="text-white/30">Role:</span><p className="text-white/60">{autofillResult.data.role}</p></div>
                              <div><span className="text-white/30">Tone:</span><p className="text-white/60">{autofillResult.data.tone}</p></div>
                              <div><span className="text-white/30">Format:</span><p className="text-white/60">{autofillResult.data.format}</p></div>
                            </div>
                            <div className="bg-dark-subtle rounded-lg p-3 border border-white/5">
                              <p className="font-mono text-xs text-white/70">{autofillResult.data.optimized_prompt}</p>
                            </div>
                            <Button variant="outline" size="sm" onClick={() => handleCopy(autofillResult.data.optimized_prompt, `af-${index}`)} className="border-white/20 text-[10px] mt-2"><Copy className="w-3 h-3 mr-1" />Copy Prompt</Button>
                          </div>
                        )}

                        {/* Timestamp */}
                        {result.last_updated && (
                          <div className="flex items-center gap-1 mt-2 text-[10px] text-white/25">
                            <Clock className="w-3 h-3" />
                            Last updated: {new Date(result.last_updated).toLocaleDateString()}
                          </div>
                        )}

                        {/* Thumbs rating + in-house review */}
                        <MatchRating toolName={result.tool?.name || ""} searchPrompt={query} />
                        <ReviewForm toolName={result.tool?.name || ""} originalPrompt={query} />
                        {(user?.subscription_tier === "team" || user?.team_id) && (
                          <button
                            onClick={async () => {
                              const res = await fetch(`${API}/team/library`, {
                                method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
                                body: JSON.stringify({ title: query.slice(0, 80), prompt: result.optimized_prompt, tool_name: result.tool?.name || "" })
                              });
                              if (res.ok) toast.success("Saved to team library!");
                              else toast.error("Failed to save");
                            }}
                            className="text-[10px] text-purple-400 hover:text-purple-300 mt-2 underline-offset-2 hover:underline"
                            data-testid={`save-to-library-${index}`}
                          >+ Save prompt to Team Library</button>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex md:flex-col gap-2 md:min-w-[100px]">
                        <a href={result.tool?.affiliate_link || result.tool?.website_url} target="_blank" rel="noopener noreferrer" className="flex-1 md:flex-none">
                          <Button className="w-full bg-purple-700 text-white hover:bg-purple-600 font-semibold text-xs">
                            Try Now <ExternalLink className="w-3.5 h-3.5 ml-1" />
                          </Button>
                        </a>
                        {result.tool?.website_url && (
                          <a href={result.tool.website_url} target="_blank" rel="noopener noreferrer" className="flex-1 md:flex-none">
                            <Button variant="outline" className="w-full border-white/20 text-xs">Visit Site</Button>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Task Decomposition */}
            {hasPremiumAccess && results.length > 0 && (
              <div className="mt-6">
                {!decomposition ? (
                  <Button onClick={handleDecompose} disabled={decomposing} variant="outline" className="w-full border-purple-500/30 text-purple-300 hover:bg-purple-900/20" data-testid="decompose-button">
                    {decomposing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Decomposing...</> : <><GitBranch className="w-4 h-4 mr-2" />Decompose into Sub-Tasks</>}
                  </Button>
                ) : (
                  <div className="bg-dark-paper border border-purple-500/20 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <GitBranch className="w-5 h-5 text-purple-400" />
                      <h3 className="font-inter font-semibold">Task Decomposition</h3>
                    </div>
                    {decomposition.project_summary && <p className="text-white/50 text-sm mb-4">{decomposition.project_summary}</p>}
                    <div className="space-y-3">
                      {(decomposition.subtasks || []).map((sub, i) => (
                        <div key={i} className="flex gap-3 p-3 bg-dark rounded-lg border border-white/5">
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-purple-900/50 border border-purple-500/30 flex items-center justify-center text-xs font-bold text-purple-300">{sub.order || i + 1}</div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="font-semibold text-sm">{sub.name}</span>
                              <Badge variant="outline" className="text-[10px] border-white/20">{sub.estimated_complexity}</Badge>
                            </div>
                            <p className="text-white/50 text-xs mb-1">{sub.description}</p>
                            <p className="text-xs"><span className="text-purple-400">Best AI:</span> <span className="text-white/70">{sub.best_ai}</span> — <span className="text-white/40">{sub.why}</span></p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {decomposition.workflow_notes && <p className="text-white/40 text-xs mt-3 italic">{decomposition.workflow_notes}</p>}
                  </div>
                )}
              </div>
            )}
            {!hasPremiumAccess && results.length > 0 && (
              <div className="mt-6 p-4 border border-white/10 rounded-xl text-center">
                <GitBranch className="w-5 h-5 text-white/20 mx-auto mb-2" />
                <p className="text-white/40 text-xs mb-2">Break complex projects into sub-tasks with AI-matched recommendations</p>
                <Link to="/pricing" className="text-purple-400 text-xs hover:underline">Upgrade to Premium for Task Decomposition</Link>
              </div>
            )}
          </div>
        )}

        {/* No results */}
        {!loading && !error && results.length === 0 && query && (
          <div className="text-center py-20">
            <Search className="w-12 h-12 text-white/15 mx-auto mb-4" />
            <h3 className="font-inter font-semibold text-lg mb-2">No tools found</h3>
            <p className="text-white/40 mb-6 text-sm">Try a different search term or wait for admin to add more AI tools.</p>
            <Link to="/"><Button variant="outline" className="border-white/20 text-sm">Back to Home</Button></Link>
          </div>
        )}
      </main>
    </div>
  );
};

export default SearchResults;
